import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Hero.css';

const Hero = () => {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(150); // 2:30 in seconds
    const [isTimerRunning, setIsTimerRunning] = useState(true);

    const demoQuestions = [
        {
            question: "What is 15% of 200?",
            options: ["25", "30", "35", "40"],
            correctAnswer: 1,
            subject: "Math",
            difficulty: "Easy"
        },
        {
            question: "Which of these is the correct translation of 'Hello' in Russian?",
            options: ["Привет", "Пока", "Спасибо", "Пожалуйста"],
            correctAnswer: 0,
            subject: "Russian",
            difficulty: "Easy"
        },
        {
            question: "Choose the correct form of the verb: 'She ___ to school every day.'",
            options: ["go", "goes", "going", "went"],
            correctAnswer: 1,
            subject: "English",
            difficulty: "Medium"
        }
    ];

    useEffect(() => {
        let timer;
        if (isTimerRunning && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0) {
            setIsTimerRunning(false);
            if (!showFeedback) {
                handleAnswer(null);
            }
        }
        return () => clearInterval(timer);
    }, [isTimerRunning, timeLeft, showFeedback]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleAnswer = (index) => {
        if (showFeedback) return; // Prevent changing answer after feedback
        setIsTimerRunning(false);
        setSelectedOption(index);
        setShowFeedback(true);
        
        if (index === demoQuestions[currentQuestion].correctAnswer) {
            setScore(score + 1);
        }
    };

    const handleNext = () => {
        if (currentQuestion < demoQuestions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
            setSelectedOption(null);
            setShowFeedback(false);
            setTimeLeft(150); // Reset timer
            setIsTimerRunning(true);
        }
    };

    const getOptionClass = (index) => {
        if (!showFeedback) return `quiz-option ${selectedOption === index ? 'selected' : ''}`;
        if (index === demoQuestions[currentQuestion].correctAnswer) return 'quiz-option correct';
        if (index === selectedOption && index !== demoQuestions[currentQuestion].correctAnswer) return 'quiz-option incorrect';
        return 'quiz-option';
    };

    return (
        <div className="hero">
            <div className="hero-content">
                <div className="hero-text">
                    <h1>Learn Smarter, Not Harder</h1>
                    <p className="hero-subtitle">
                        A completely free learning platform for students. Practice with interactive quizzes and get instant feedback to improve your knowledge.
                    </p>
                    
                    <div className="hero-stats">
                        <div className="stat-item">
                            <span className="stat-number">50+</span>
                            <span className="stat-label">Practice Questions</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">3</span>
                            <span className="stat-label">Core Subjects</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">100%</span>
                            <span className="stat-label">Free Forever</span>
                        </div>
                    </div>

                    <div className="hero-buttons">
                        <Link to="/signup" className="primary-button">
                            Get Started
                        </Link>
                        <Link to="/signin" className="secondary-button">
                            Sign In
                        </Link>
                    </div>
                </div>

                <div className="hero-preview">
                    <div className="quiz-preview">
                        <div className="quiz-header">
                            <span className="subject-badge">{demoQuestions[currentQuestion].subject}</span>
                            <span className="difficulty-badge">{demoQuestions[currentQuestion].difficulty}</span>
                        </div>
                        
                        <div className="quiz-progress">
                            <div className="progress-bar">
                                <div 
                                    className="progress" 
                                    style={{ 
                                        width: `${((currentQuestion + 1) / demoQuestions.length) * 100}%` 
                                    }}
                                ></div>
                            </div>
                            <span className="progress-text">Question {currentQuestion + 1} of {demoQuestions.length}</span>
                        </div>

                        <div className={`quiz-timer ${timeLeft <= 30 ? 'warning' : ''}`}>
                            <span className="material-icons">timer</span>
                            <span>{formatTime(timeLeft)}</span>
                        </div>

                        <div className="quiz-question">
                            <h3>{demoQuestions[currentQuestion].question}</h3>
                            <div className="quiz-options">
                                {demoQuestions[currentQuestion].options.map((option, index) => (
                                    <button
                                        key={index}
                                        className={getOptionClass(index)}
                                        onClick={() => handleAnswer(index)}
                                        disabled={showFeedback}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {showFeedback && (
                            <div className="quiz-feedback">
                                {selectedOption === demoQuestions[currentQuestion].correctAnswer ? (
                                    <>
                                        <span className="material-icons success">check_circle</span>
                                        <span>Correct! Well done!</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="material-icons error">error</span>
                                        <span>Not quite right. The correct answer is: {demoQuestions[currentQuestion].options[demoQuestions[currentQuestion].correctAnswer]}</span>
                                    </>
                                )}
                                {currentQuestion < demoQuestions.length - 1 && (
                                    <button className="next-button" onClick={handleNext}>
                                        Next Question
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Hero; 